import { DOCUMENT_CHECK_IDS } from '@qim/shared'
import { AccountLink, mayOpenAccount } from './account-link.jsx'
import type { JSX } from 'react'
import type { PreflightCheck, PreflightResult } from '@qim/shared'

/**
 * What the account says about a run before it is started (IM-36).
 *
 * The API decides every check; this renders them. Only the checks the form
 * cannot compute itself are shown — the document checks are the same functions
 * the form already runs on every keystroke, and saying each refusal twice, once
 * instantly and once a round trip later, would make the second one noise.
 *
 * **Word and shape first, colour third.** Each check says Blocks, Warning or
 * Unknown in words and carries a glyph that differs by shape, so the list reads
 * the same on a projector, in a screenshot pasted into a ticket, and to someone
 * who cannot tell the two colours apart.
 */

/** The checks this panel shows, which are the ones the form does not already. */
export const shownChecks = (result: PreflightResult): readonly PreflightCheck[] =>
  result.checks.filter((c) => !DOCUMENT_CHECK_IDS.has(c.id))

/** Whether a check stops the run, in the preflight's own terms. */
export const isBlockingFailure = (check: PreflightCheck): boolean =>
  check.severity === 'blocking' && check.status === 'fail'

const LABEL = {
  blocks: { word: 'Blocks', glyph: '✕', className: 'preflight-blocks' },
  warning: { word: 'Warning', glyph: '!', className: 'preflight-warning' },
  unknown: { word: 'Unknown', glyph: '?', className: 'preflight-unknown' },
} as const

const labelFor = (check: PreflightCheck): (typeof LABEL)[keyof typeof LABEL] =>
  isBlockingFailure(check)
    ? LABEL.blocks
    : check.status === 'unknown'
      ? LABEL.unknown
      : LABEL.warning

/**
 * Where an account check is fixed, when the caller can navigate there (IM-181).
 *
 * Every `account` check reads the onboarding assessment's own steps, so the
 * screen that hands over the template for each of them is the account's
 * Onboarding tab. The region picker there is local state rather than a route,
 * so this lands on the tab and the region is chosen on arrival.
 */
export interface OnboardingTarget {
  readonly tenantId: string
  readonly accountKey: string
  readonly role: string
  readonly onOpen: (path: string) => void
}

const Item = ({
  check,
  onboarding,
}: {
  readonly check: PreflightCheck
  readonly onboarding?: OnboardingTarget | undefined
}): JSX.Element => {
  const label = labelFor(check)
  return (
    <li className={`preflight-item ${label.className}`}>
      <span className="preflight-mark">
        <span aria-hidden="true">{label.glyph}</span> {label.word}
      </span>{' '}
      <strong>{check.title}</strong> &mdash; {check.detail}
      {check.fix === undefined ? null : <p className="preflight-fix">{check.fix}</p>}
      {/* Only the account's checks: a run's own document is fixed in the form
        this panel sits in, and a link away from it would lose what was typed
        for nothing. */}
      {onboarding !== undefined &&
      check.basis === 'account' &&
      check.status !== 'pass' &&
      mayOpenAccount(onboarding.role) ? (
        <p className="preflight-fix">
          <AccountLink
            tenantId={onboarding.tenantId}
            accountKey={onboarding.accountKey}
            role={onboarding.role}
            onOpen={onboarding.onOpen}
          >
            Fix this on the account&rsquo;s onboarding screen
            <span aria-hidden="true"> →</span>
          </AccountLink>
        </p>
      ) : null}
    </li>
  )
}

export const Preflight = ({
  result,
  checking,
  failed,
  onboarding,
}: {
  /** Where an account check is fixed; absent offers the fix in words only. */
  readonly onboarding?: OnboardingTarget | undefined
  /** The answer for the run as it is now, when one has arrived. */
  readonly result?: PreflightResult | undefined
  /** A request is in flight for the run as it is now. */
  readonly checking: boolean
  /** The last request failed, so nothing is known about the account. */
  readonly failed: boolean
}): JSX.Element => {
  if (result === undefined) {
    return (
      <section className="preflight" aria-label="Before this run">
        <p className="hint">
          {checking
            ? 'Checking the account before this run can start…'
            : failed
              ? 'The account could not be checked just now. The run can still be started, and the account will refuse it if it cannot run.'
              : 'Not checked yet.'}
        </p>
      </section>
    )
  }

  const shown = shownChecks(result)
  const blocking = shown.filter(isBlockingFailure)
  const advisory = shown.filter((c) => c.status !== 'pass' && !isBlockingFailure(c))
  const passed = shown.length - blocking.length - advisory.length

  return (
    <section className="preflight" aria-label="Before this run">
      {blocking.length === 0 ? null : (
        <>
          <h3 className="preflight-heading">Stops this run</h3>
          <ul className="preflight-list">
            {blocking.map((c) => (
              <Item key={c.id} check={c} onboarding={onboarding} />
            ))}
          </ul>
        </>
      )}
      {advisory.length === 0 ? null : (
        <>
          <h3 className="preflight-heading">Worth knowing</h3>
          <ul className="preflight-list">
            {advisory.map((c) => (
              <Item key={c.id} check={c} onboarding={onboarding} />
            ))}
          </ul>
        </>
      )}
      <p className="hint">
        {passed === shown.length
          ? `The account is ready for this run in ${result.region}: ${String(passed)} checks passed.`
          : `${String(passed)} of ${String(shown.length)} account checks passed for ${result.region}.`}
        {checking ? ' Re-checking…' : ''}
      </p>
    </section>
  )
}
